import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { HostPrimaryButton, HostSettingsCard } from '../../components/HostSettingsCard';
import { HostSettingsLayout } from '../../components/HostSettingsLayout';
import { fetchAccountSettings, updateAccountProfile } from '../../utils/accountSettingsApi';
import { titleCaseName } from '../../utils/nameFormatters';
import { formatLastLetters, formatFirstName } from '../../utils/nameFormat';

const fieldLabelStyle = { fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', textAlign: 'center', margin: 0 };
const fieldInputStyle = { fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', width: '100%', border: 'none', outline: 'none', background: 'transparent', padding: '4px 0' };
const emailValueStyle = { fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)', textAlign: 'center', width: '100%', padding: '8px 0 4px', margin: 0, wordBreak: 'break-all', overflowWrap: 'anywhere', lineHeight: 1.25 };

export default function GuestPersonalInfoPage() {
  const [form, setForm] = useState({ first_name: '', last_name: '' });
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const settings = await fetchAccountSettings();
        setForm({ first_name: formatFirstName(settings.profile.first_name || ''), last_name: formatLastLetters(settings.profile.last_name) });
        setEmail(settings.security?.email || JSON.parse(localStorage.getItem('user') || '{}').email || '');
      } catch (error) {
        toast.error('Failed to load personal information');
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Always store names with consistent title-casing so chat surfaces
      // ("Nikos P.") and other lists look polished.
      const cleaned = {
        first_name: titleCaseName(form.first_name),
        last_name: formatLastLetters(form.last_name),
      };
      setForm(cleaned);
      const response = await updateAccountProfile(cleaned);
      const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...existingUser,
        name: `${response.profile.first_name} ${response.profile.last_name}`.trim() || existingUser.name,
      }));
      toast.success('Personal information updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <HostSettingsLayout title="Personal Information" dataTestId="guest-personal-info-page">
      <HostSettingsCard data-testid="guest-personal-info-card" style={{ padding: '20px 18px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <p style={fieldLabelStyle}>First Name</p>
            <input data-testid="guest-first-name-input" value={form.first_name} onChange={(event) => setForm((prev) => ({ ...prev, first_name: formatFirstName(event.target.value) }))} onBlur={(event) => setForm((prev) => ({ ...prev, first_name: titleCaseName(event.target.value) }))} style={fieldInputStyle} />
            <div style={{ borderBottom: '2px dotted #8B8B8B', marginTop: 2 }} />
          </div>
          <div>
            <p style={fieldLabelStyle}>Last Name</p>
            <input data-testid="guest-last-name-input" value={form.last_name ? `${form.last_name}.` : ''} onChange={(event) => setForm((prev) => ({ ...prev, last_name: formatLastLetters(event.target.value) }))} onKeyDown={(event) => { if (event.key === 'Backspace') { event.preventDefault(); setForm((prev) => ({ ...prev, last_name: '' })); } }} placeholder="K." style={fieldInputStyle} />
            <div style={{ borderBottom: '2px dotted #8B8B8B', marginTop: 2 }} />
          </div>
          <div>
            <p style={fieldLabelStyle}>E-mail</p>
            <p data-testid="guest-email-value" style={emailValueStyle}>
              {email || '—'}
            </p>
            <div style={{ borderBottom: '2px dotted #8B8B8B', marginTop: 2 }} />
          </div>
        </div>
      </HostSettingsCard>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
        <HostPrimaryButton onClick={handleSave} dataTestId="guest-personal-info-save-button" disabled={saving}>{saving ? 'Saving...' : 'DONE'}</HostPrimaryButton>
      </div>
    </HostSettingsLayout>
  );
}